import React, { useState, useEffect } from 'react';
import { Card, Table, Typography, Spin, Alert, Tag, Collapse, List } from 'antd';
import { RightOutlined, DownOutlined, FileOutlined, FileImageOutlined, FilePdfOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { WarrantyHistoryItem, WarrantyHistoryApiResponse, TicketFile } from '../types/ticket';
import { apiService } from '../services/api';
import moment from 'moment';
import FileAttachment from './FileAttachment';

const { Text } = Typography;
const { Panel } = Collapse;

interface WarrantyHistoryProps {
  serialNumber: string;
  token?: string | null;
}

const WarrantyHistory: React.FC<WarrantyHistoryProps> = ({ serialNumber, token }) => {
  const [warrantyData, setWarrantyData] = useState<WarrantyHistoryApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchWarrantyHistory = async (page: number = 1, size: number = 10) => {
    try {
      setLoading(true);
      const response = await apiService.getWarrantyHistory(serialNumber, page, size, token);
      setWarrantyData(response);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load warranty history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (serialNumber) {
      fetchWarrantyHistory(currentPage, pageSize);
    }
  }, [serialNumber, token, currentPage, pageSize]);

  const getStatusColor = (status: number) => {
    const statusMap: { [key: number]: string } = {
      1: 'default',   // WAIT_RECEIPT
      2: 'processing', // WAIT_PROCESS
      3: 'processing', // PROCESSING
      4: 'success',    // COMPLETED
      5: 'error',      // REJECTED
    };
    return statusMap[status] || 'default';
  };

  const getStatusText = (status: number) => {
    const statusMap: { [key: number]: string } = {
      1: 'Waiting for Receipt',
      2: 'Waiting for Processing',
      3: 'Processing',
      4: 'Completed',
      5: 'Rejected',
    };
    return statusMap[status] || 'Unknown';
  };

  const getProcessingStatusColor = (status: number) => {
    const statusMap: { [key: number]: string } = {
      1: 'default',   // WAIT_APPROVE
      2: 'processing', // WAIT_PROCESS
      3: 'processing', // PROCESSING
      4: 'success',    // PROCESSED
      5: 'warning',    // WAIT_CONFIRM
      6: 'default',    // PENDING
      7: 'success',    // COMPLETED
      8: 'error',      // CANCELLED
      9: 'processing', // PROCESSING_AGAIN
    };
    return statusMap[status] || 'default';
  };

  const getProcessingStatusText = (status: number) => {
    const statusMap: { [key: number]: string } = {
      1: 'Waiting for Approval',
      2: 'Waiting for Processing',
      3: 'Processing',
      4: 'Processed',
      5: 'Waiting for Confirmation',
      6: 'Pending',
      7: 'Completed',
      8: 'Cancelled',
      9: 'Processing Again',
    };
    return statusMap[status] || 'Unknown';
  };

  const columns = [
    {
      title: 'Request Date',
      dataIndex: 'requestDate',
      key: 'requestDate',
      render: (date: string) => (
        <div style={{ lineHeight: 1.2, paddingTop: 4 }}>
          <div style={{ color: '#888', fontSize: 12, fontWeight: 500 }}>{moment(date).format('DD/MM/YYYY')}</div>
          <div style={{ fontSize: 12 }}>{moment(date).format('HH:mm')}</div>
        </div>
      ),
    },
    {
      title: 'Solution',
      dataIndex: 'solution',
      key: 'solution',
      render: (solution: any, record: WarrantyHistoryItem) => (
        <div>
          <div>
            <Text>{solution?.description || 'No solution provided'}</Text>
          </div>
          {record.processBranchName && (
            <div style={{ marginTop: '4px' }}>
              <Text type="secondary">By {record.processBranchName}</Text>
            </div>
          )}
          {record.metadata?.fileAttachments && record.metadata.fileAttachments.length > 0 && (
            <FileAttachment 
              files={record.metadata.fileAttachments}
              token={token}
              className="warranty-attachments"
            />
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: number) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
  ];

  // Custom panel header with expand/collapse icon
  const panelHeader = () => (
    <div className="card-title-with-icon">
      <div className="card-title">
        Warranty History ({warrantyData?.data?.totalItem || 0} records)
      </div>
      <div className="card-expand-icon">
        {isExpanded ? <DownOutlined /> : <RightOutlined />}
      </div>
    </div>
  );

  if (error) {
    return (
      <Card className="app-card">
        {panelHeader()}
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ margin: '16px' }}
        />
      </Card>
    );
  }

  return (
    <Card 
      className="app-card warranty-history-card"
    >
      <div onClick={() => setIsExpanded(!isExpanded)}>
        {panelHeader()}
      </div>
      {isExpanded && (
        <div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px' }}>
                <Text>Loading warranty history...</Text>
              </div>
            </div>
          ) : warrantyData?.data?.tickets?.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Text type="secondary">No warranty history found</Text>
            </div>
          ) : (
            <Table
              dataSource={warrantyData?.data?.tickets || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: warrantyData?.data?.totalItem || 0,
                showSizeChanger: false,
                showQuickJumper: false,
                onChange: (page) => {
                  setCurrentPage(page);
                },
              }}
              rowKey="ticketId"
              size="small"
              className="warranty-history-table"
            />
          )}
        </div>
      )}
    </Card>
  );
};

export default WarrantyHistory; 